/**
 * Полоса прогресса папки эфира в редакторе переводчика (W4.4, фаза 4) — данные и тексты, без React.
 *
 * Источник — translate-python `GET /stream_editor/v1/progress?media=` (переводчику из списка): по папке и языку
 * четыре шага «русский — перевод — голос — в эфире» и «зрители увидят в ЧЧ:ММ» (начало речи папки + задержка
 * зрителей), правки переводчика по папке (заявки полосы правок site_server и их отчёты).
 */
import { cuesRu, fixCueCounts, pluralRu } from "../../../../../utils/pluralRu";

export type StepState =
    | "done"
    | "running"
    | "queued"
    | "failed"
    | "held"
    | "degraded"
    | "updating"
    | "pending";

export interface StreamFix {
    id: string;
    state: string;
    stage?: string;
    kind?: string;
    found?: string;
    /** Что сделал редактор в заявке `text_changed` (site_server `EDITOR_EDITS`): revoice — ♪, text — текст… */
    edit?: string;
    folder?: number;
    lang?: string;
    cue_ids?: string[];
    /** Откуда заявка (`ai` — замена ИИ: вопрос «сокращу сам» она не закрывает, как и в TAG). */
    source?: string;
    /** Реплики, какими они стали после правки (site_server `edit_side_effects.own_cues`): `{новая: прежняя}`. */
    reopened?: Record<string, string> | null;
    /** Куски раскладки: `{фраза: [куски]}`. */
    relaid?: Record<string, string[]> | null;
    text_changed?: string[];
    revoiced?: string[];
    by?: string;
    requested_at?: string;
    settle_until?: string;
    subs_published_at?: string | null;
    published_at?: string | null;
    public_at?: string | null;
    to_record_only?: boolean | null;
    hls_revision?: string | null;
    error?: string | null;
    summary_ru?: string;
    finished_at?: string;
    /**
     * Правка времени (`edit: time`): что человек сделал со временем реплик — {реплика: {from?, to?, want_length?,
     * dub_ms?}} (site_server `edit_timing`). `to` — новое место; `want_length` — растянутая длина озвученного дубля,
     * которую сервер НЕ пишет (её задаёт звук), `dub_ms` — сколько дубль звучит.
     */
    timing?: Record<string, LiveTimingSpec>;
    /**
     * `needs_translator`: вопрос переводчику (site_server `stream_edits`) — правка не влезла и временем (п.50г) или
     * правка времени увела реплику дальше предела от слова лектора (`cause.kind: edit_drift`). Кнопки ответа — ровно
     * `question.answers`.
     */
    question?: FixQuestion | null;
    /** Ответ на вопрос: кнопкой или сервером (`resolved` — новая правка той же реплики прошла, `probe_retired`). */
    answered?: FixAnswered | null;
    restored?: boolean;
    /**
     * Что переводчик назвал неверным (корявым), а перевод заново оставил ТЕМИ ЖЕ словами (site_server
     * `stream_edits.marked_same`, проба P50m20f, E5). Полоса translate-python старше поля — его нет: то же говорит отчёт
     * (`summary_ru`: «не исправлено: кусок 2 из 3 … остался прежним»), см. `markedSameOf`.
     */
    marked_same?: MarkedSame[] | null;
    /**
     * Правка пришла во время прохода своей папки и ждёт его конца — номер папки (site_server `with_pass_wait`,
     * проба P50m10e: ♪ ждал 114 с, а тег, тост и шапка говорили «в работе»). Круг начнётся сразу после прохода.
     */
    waits_folder_pass?: number | null;
    /**
     * Что СЕЙЧАС в эфире от этой правки (site_server `on_air`, договор круга AI): по нему — короткий статус. Полоса
     * translate-python старше поля — его нет: тогда по меткам выкладки и откату (`onAirOf`), как плеер.
     */
    on_air?: FixOnAir | null;
    /** Откат круга (site_server `round_revert`): в полосе его может не быть — тогда `restored` и слова отката в `error`. */
    round_revert?: FixRoundRevert | null;
}

/**
 * Что сейчас в эфире от правки (договор круга AI, site_server `on_air`):
 *  * `none` — ничего (в эфире прежняя версия);
 *  * `subs_only` — только субтитры правки (голос не вышел);
 *  * `full` — правка целиком;
 *  * `partial` — часть правки (например, другие куски фразы);
 *  * `old` — откат вернул прежнее;
 *  * `subs_stuck` — откат вернул папку, а прежние субтитры не выложены: зритель может видеть субтитры правки.
 * `subs_at` / `media_at` — когда вышли субтитры / звук правки, `why_ru` — почему так, словами сервера.
 */
export type OnAirKind = "none" | "subs_only" | "full" | "partial" | "old" | "subs_stuck";

export interface FixOnAir {
    kind?: OnAirKind | string | null;
    subs_at?: string | null;
    media_at?: string | null;
    why_ru?: string | null;
}

/** Откат круга (site_server `round_revert`) — поля, которые читает редактор. */
export interface FixRoundRevert {
    restored?: boolean;
    reached_air?: string | null;
    words?: string | null;
    subs_restored?: string | null;
    subs_error?: string | null;
}

/**
 * Кусок фразы (или реплика), который переводчик назвал неверным, а перевод заново оставил прежним: кусок `index` из
 * `of`, прежний текст `text`, почему `why_ru`; `cue` — кусок, на который показали, `now` — кусок на его месте сейчас
 * (фразу разложили заново — у куска новое имя).
 */
export interface MarkedSame {
    cue?: string | null;
    parent?: string | null;
    index?: number | null;
    of?: number | null;
    text?: string | null;
    now?: string | null;
    why_ru?: string | null;
}

export interface FixQuestion {
    text_ru?: string;
    short_ms?: number | null;
    neighbors?: { id: string; text?: string | null; shorter_ms?: number | null }[];
    /** Текст правки, который не влез: `{реплика: текст}` (папка откатана — на ленте его уже нет). */
    proposed?: Record<string, string> | null;
    /** Реплики самой правки с перебором (их сократить может только переводчик). */
    own?: { id: string; text?: string | null }[] | null;
    answers?: string[];
    restored?: boolean;
    on_air_ru?: string;
    cause?: { kind?: string | null; cue_id?: string | null; ms?: number | null; limit_ms?: number | null } | null;
}

export interface FixAnswered {
    answer?: string;
    /** У `resolved`: `time` — новое место, `edit` — правка текста, `keep` — «оставить как есть», `in_limit`. */
    how?: string | null;
    fix_id?: string | null;
    by?: string | null;
    at?: string;
    auto?: boolean;
}

export interface LiveTimingSpec {
    from?: number | null;
    to?: number | null;
    want_length?: number | null;
    dub_ms?: number | null;
    /** Длина НЕозвученной реплики, которую человек растянул: записана в папку, в эфир не уходит (звука нет). */
    length?: { from?: number | null; to?: number | null } | null;
}

/** Правдивая половина тоста о длине неозвученной реплики (03.11). */
export const UNVOICED_LENGTH_ON_DISK = "длина неозвученной реплики записана в папку — звука у неё нет, в эфир она не уходит";

/** Правдивая половина тоста о растянутом дубле (проба P50m10d, 03.11). */
export const DUB_LENGTH_IS_ITS_SOUND = "длину дубля задаёт его звук; сжать или растянуть дубль — темп ♪";

export interface ProgressLang {
    stage?: string;
    state?: string;
    degraded?: boolean;
    published_at?: string | null;
    steps: Record<string, StepState> & { current?: string; title?: string };
    fixes: StreamFix[];
    fixes_open: number;
    /** Из открытых — сколько ждут конца прохода папки (translate-python `fixes_wait_pass`). */
    fixes_wait_pass?: number;
}

export interface ProgressFolder {
    folder: number;
    closed_at?: string | null;
    air_start_at?: string | null;
    public_at?: string | null;
    langs: Record<string, ProgressLang>;
    ru_sync?: { status?: string; mode?: string; languages?: Record<string, { status?: string; error?: string }> };
}

export interface StreamProgressData {
    media: string;
    open?: boolean;
    closed?: boolean;
    strict?: boolean;
    held?: boolean;
    held_folders?: number[];
    viewer_delay_sec?: number | null;
    steps: { id: string; title: string }[];
    folders: ProgressFolder[];
    fixes_error?: string;
}

export const STEP_TITLES: Record<string, string> = {
    ru: "русский",
    translate: "перевод",
    voice: "голос",
    air: "в эфире",
};

export const STATE_MARK: Record<StepState, string> = {
    done: "✓",
    running: "⟳",
    queued: "…",
    failed: "✕",
    held: "⏸",
    degraded: "!",
    updating: "⟳",
    pending: "·",
};

export const STATE_COLOR: Record<StepState, string> = {
    done: "green",
    running: "processing",
    queued: "default",
    failed: "error",
    held: "warning",
    degraded: "warning",
    updating: "processing",
    pending: "default",
};

export const OPEN_FIX_STATES = ["queued", "settling", "running"];
/**
 * `needs_translator` (п.50г, 02.11): правка не влезла и временем, а соседние реплики без согласия переводчика не
 * сокращаются — круг откатил папку (в эфире прежняя версия) и спрашивает: сократить свой текст или разрешить сократить
 * соседнюю. Правка времени дальше предела от слова лектора (03.11) — так же: вопрос, в эфире прежнее место; ответ —
 * новое место или «Оставить как в эфире». Кнопки — ровно `question.answers` (тост вопроса и «Эфир: папки и правки»).
 */
export const ASK_FIX_STATE = "needs_translator";
export const FINAL_FIX_STATES = ["fixed", "failed", "nothing_to_fix", "refused", ASK_FIX_STATE];

/** Язык пары в ответе: редактор зовёт китайский «ch», эфир — «zh» (и наоборот). */
export const langOf = (folder: ProgressFolder | undefined, lang: string): ProgressLang | undefined => {
    if (!folder) return undefined;
    const aliases = lang === "ch" ? ["ch", "zh"] : lang === "zh" ? ["zh", "ch"] : [lang];
    for (const name of aliases) {
        if (folder.langs?.[name]) return folder.langs[name];
    }
    return undefined;
};

/** ЧЧ:ММ местного времени. */
export const hhmm = (iso?: string | null): string | null => {
    if (!iso) return null;
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return null;
    return `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
};

/** «зрители увидят в ЧЧ:ММ» / «зрители уже видят (с ЧЧ:ММ)». */
export const viewersLine = (publicAt?: string | null, now: number = Date.now()): string | null => {
    const at = hhmm(publicAt);
    if (!at || !publicAt) return null;
    return new Date(publicAt).getTime() > now ? `зрители увидят в ${at}` : `зрители видят с ${at}`;
};

const ISO_TIME = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?/g;
/** «09:53 UTC» в словах сервера (`on_air.why_ru`, отчёт отката). */
const UTC_CLOCK = /\b(\d{1,2}):(\d{2}) UTC\b/g;

/**
 * Время отчёта сервера — ЧЧ:ММ местного времени: ISO («в эфире: 18:20; зрители увидят: 18:40») и «09:53 UTC» (слова
 * отката и `on_air`), как плеер (site_eng `utcClocksInText`): статус «субтитры правки выходили в 12:53» и отчёт под ним
 * говорят одно время. День «ЧЧ:ММ UTC» — по `ref` (время той же заявки), без него — сегодняшний.
 */
export const localTimes = (text: string, ref?: string | null): string => {
    const base = ref ? new Date(String(ref).replace(/(\.\d{3})\d+/, "$1")) : new Date();
    const day = Number.isNaN(base.getTime()) ? new Date() : base;
    return text
        .replace(ISO_TIME, (iso) => hhmm(iso) || iso)
        .replace(UTC_CLOCK, (all: string, h: string, m: string) => {
            const t = new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), Number(h), Number(m)));
            return Number.isNaN(t.getTime()) ? all : hhmm(t.toISOString()) || all;
        });
};

/** Время заявки — день для «ЧЧ:ММ UTC» в её словах. */
const fixRef = (fix: StreamFix): string | null =>
    fix.on_air?.subs_at || fix.on_air?.media_at || fix.subs_published_at || fix.published_at || fix.finished_at ||
    fix.requested_at || null;

/** Папка, конца чьего прохода ждёт правка (`waits_folder_pass`); null — не ждёт (или полоса старше поля). */
export const passWaitOf = (fix: { waits_folder_pass?: number | null } | null | undefined): number | null => {
    const n = fix?.waits_folder_pass;
    return typeof n === "number" && Number.isFinite(n) && n > 0 ? n : null;
};

/** «ждёт конца прохода папки 1 — круг правки начнётся сразу после него» — тост, тег и шапка говорят одно. */
export const passWaitLine = (folder: number): string =>
    `ждёт конца прохода папки ${folder} — круг правки начнётся сразу после него`;

/** Тег правки, что ждёт прохода папки, — коротко. */
export const passWaitTag = (folder: number): string => `ждёт прохода папки ${folder}`;

/**
 * Из открытых правок папки — сколько заменяет ИИ (`source: ai`, «замена по решению ИИ»). Проба P50m20d, папка 1: за
 * 4 с до ♪ переводчика ИИ завёл две замены — шапка после одного ♪ сказала «правок в работе: 3», после второго — «4», и
 * переводчик решил, что каждый ♪ считается дважды. Заявка ♪ одна (fx0067f97dcae544, fx5fadc90f18cd4b); замены ИИ
 * шапка теперь называет. Не больше открытых всего (`fixes_open`): полоса отдаёт не все правки папки.
 */
export const openAiFixes = (lang: Pick<ProgressLang, "fixes" | "fixes_open"> | undefined): number => {
    if (!lang) return 0;
    const ai = (lang.fixes || []).filter(
        (fx) => OPEN_FIX_STATES.includes(fx.state) && String(fx.source || "") === "ai",
    ).length;
    return Math.min(ai, Math.max(0, lang.fixes_open || 0));
};

/**
 * Счётчик шапки: «правок в работе: 2»; сколько из них — замены ИИ и сколько ждут прохода папки, — так и сказано
 * (все — «— замены ИИ», «— ждут конца прохода папки 1»; часть — «, из них замен ИИ: 2»).
 */
export const openFixesLine = (open: number, waiting: number, folder: number, ai: number = 0): string => {
    if (open <= 0) return "";
    const all: string[] = [];
    const some: string[] = [];
    if (ai >= open) all.push(open === 1 ? "замена ИИ" : "замены ИИ");
    else if (ai > 0) some.push(`замен ИИ: ${ai}`);
    if (waiting >= open) all.push(`ждут конца прохода папки ${folder}`);
    else if (waiting > 0) some.push(`ждут конца прохода папки ${folder}: ${waiting}`);
    return (
        `правок в работе: ${open}` +
        (all.length ? ` — ${all.join(", ")}` : "") +
        (some.length ? `, из них ${some.join(", ")}` : "")
    );
};

/**
 * Тост «Правка принята»: что уйдёт в эфир. У ♪ — новый дубль, а не «перевод и голос» (проба P50m10d, E6: ♪ — это
 * переозвучка, текст не менялся).
 */
const acceptedWhat = (fix: StreamFix): string => {
    const what = fix.found || fix.kind || "правка";
    switch (fix.edit) {
        case "revoice":
            return `${what}: уйдёт в эфир сама — новый дубль реплики, новая ревизия`;
        case "skip":
        case "unskip":
        case "delete_audio":
            return `${what}: уйдёт в эфир сама — звук папки без пересинтеза, новая ревизия`;
        case "time": {
            // Правка времени (liveTiming.ts, проба P50m10d): соседей подгоняет временем и сверяет с лектором сервер.
            // Растянутый озвученный дубль (`want_length`) в эфир не уходит — его длину задаёт звук (03.11): тост
            // говорит это, а не «новое место реплики».
            const specs = Object.values(fix.timing || {});
            const moved = specs.some((s) => typeof s?.to === "number");
            const stretched = specs.some((s) => typeof s?.want_length === "number");
            const unvoiced = specs.some((s) => typeof s?.length?.to === "number");
            const place = `${what}: уйдёт в эфир сама — новое место реплики, соседей подгонит временем круг правки, новая ревизия`;
            const notes = [
                ...(stretched ? [DUB_LENGTH_IS_ITS_SOUND] : []),
                ...(unvoiced ? [UNVOICED_LENGTH_ON_DISK] : []),
            ];
            if (!moved && notes.length) return `${what}: в эфир ничего не уходит — ${notes.join("; ")}`;
            return notes.length ? `${place}; ${notes.join("; ")}` : place;
        }
        default:
            return `${what}: уйдёт в эфир сама — перевод и голос правленых реплик, новая ревизия`;
    }
};

export const acceptedLine = (fix: StreamFix): string => {
    const line = acceptedWhat(fix);
    const wait = passWaitOf(fix);
    // Пришла во время прохода своей папки: круг правки начнётся после него — тост говорит это сразу (P50m10e).
    return wait ? `${line}; сейчас ${passWaitLine(wait)}` : line;
};

/** Отчёт правки одной строкой для тоста. */
export const fixLine = (fix: StreamFix): string => {
    // Отчёт сервера, написанный до исправления склонения, — «(1 реплик)» склоняется здесь (P50m10).
    if (fix.summary_ru) return localTimes(fixCueCounts(fix.summary_ru), fixRef(fix));
    const what = fix.found || fix.kind || "правка";
    // Названное переводчиком неверным осталось прежним — не «в эфире» и не «менять нечего» (P50m20f, E5).
    const same = markedSameOf(fix);
    if (same.length) return `${what}: не исправлено: ${same.map(markedSameWhat).join("; ")}`;
    if (fix.state === "fixed") {
        const when = fix.to_record_only
            ? "уже показанная часть — исправлено в записи"
            : viewersLine(fix.public_at) || "в эфире";
        return `${what}: в эфире ${hhmm(fix.published_at || fix.subs_published_at) || ""} — ${when}`.trim();
    }
    if (fix.state === "refused" || fix.state === "failed") return `${what}: ${fix.error || fix.state}`;
    if (fix.state === "nothing_to_fix") return `${what}: менять нечего`;
    return `${what}: ${fix.stage || fix.state}`;
};

// ── Что сейчас в эфире от правки (договор круга AI: `on_air` сервера; без него — как плеер, по откату и меткам) ────

const ON_AIR_KINDS: OnAirKind[] = ["none", "subs_only", "full", "partial", "old", "subs_stuck"];

/** Слова отката: прежние субтитры снова не выложены (site_server `round_revert_report`, дописаны к `error`). */
const SUBS_LEFT_RU = "прежние субтитры снова не выложены";
/** Слова отката, который нельзя сделать: круг дошёл до заливки в облако (site_server `revert_failed_round`). */
const PART_ON_AIR_RU = "в эфире может быть часть правки";

export interface FixAir {
    kind: OnAirKind;
    /** Когда вышли субтитры правки / её звук (ISO). */
    subsAt: string | null;
    mediaAt: string | null;
    /** Почему так — словами сервера (`on_air.why_ru`); без поля — "". */
    why: string;
    /** Взято из `on_air` сервера (а не выведено по старому отчёту). */
    fromServer: boolean;
}

/** Часть «исправлено: …» отчёта сервера (`summary_ru`); нет её — "". */
const summaryFixedPart = (summary?: string | null): string => {
    const text = String(summary || "");
    const head = /(?:^|; )исправлено: /.exec(text);
    if (!head) return "";
    let depth = 0;
    let quote = 0;
    const start = head.index + head[0].length;
    for (let i = start; i < text.length; i++) {
        const ch = text[i];
        if (ch === "«") quote += 1;
        else if (ch === "»") quote = Math.max(0, quote - 1);
        else if (!quote && ch === "(") depth += 1;
        else if (!quote && ch === ")") depth = Math.max(0, depth - 1);
        else if (ch === ";" && text[i + 1] === " " && !quote && !depth) return text.slice(start, i).trim();
    }
    return text.slice(start).trim();
};

/** Правка что-то сменила в эфире (текст или голос) — по итогу круга; полоса без итога — по «исправлено: …» отчёта. */
const changedOnAir = (fix: StreamFix): boolean => {
    if ((fix.text_changed || []).length + (fix.revoiced || []).length > 0) return true;
    const part = summaryFixedPart(fix.summary_ru);
    return Boolean(part) && part !== "нет" && !part.startsWith("менять нечего");
};

/**
 * Что сейчас в эфире от правки. Поле `on_air` сервера — как есть. Без него (полоса или сервер старше договора) — как
 * плеер (`failedRoundReport`): у упавшей заявки по откату (`round_revert`, у полосы — `restored` и слова отката в
 * `error`) и меткам выкладки; у правки в эфире, чей кусок остался прежним (`marked_same`), — часть правки.
 */
export const onAirOf = (fix: StreamFix): FixAir => {
    const on = fix.on_air;
    if (on && typeof on === "object" && ON_AIR_KINDS.includes(String(on.kind) as OnAirKind)) {
        return {
            kind: on.kind as OnAirKind,
            subsAt: on.subs_at || null,
            mediaAt: on.media_at || null,
            why: String(on.why_ru || "").trim(),
            fromServer: true,
        };
    }
    const subsAt = fix.subs_published_at || null;
    const mediaAt = fix.published_at || null;
    const air = (kind: OnAirKind): FixAir => ({ kind, subsAt, mediaAt, why: "", fromServer: false });
    if (fix.state === "failed" || fix.state === "refused") {
        const rr = fix.round_revert && typeof fix.round_revert === "object" ? fix.round_revert : null;
        const restored = rr && typeof rr.restored === "boolean" ? rr.restored : fix.restored;
        const words = `${rr?.words || ""} ${fix.error || ""} ${fix.summary_ru || ""}`;
        if (restored === true) {
            const left = Boolean(rr?.subs_error) || words.indexOf(SUBS_LEFT_RU) >= 0;
            if (subsAt && left) return air("subs_stuck");
            return air(subsAt ? "old" : "none");
        }
        if ((rr && rr.reached_air) || words.indexOf(PART_ON_AIR_RU) >= 0) return air("partial");
        if (mediaAt) return air("full");
        if (subsAt) return air("subs_only");
        return air("none");
    }
    if (fix.state === "fixed") {
        if (String(fix.kind || "") === "keep" || !changedOnAir(fix)) return air("none");
        return air(markedSameOf(fix).length ? "partial" : "full");
    }
    return air(mediaAt || subsAt ? (mediaAt ? "full" : "subs_only") : "none");
};

/** У упавшей правки: что в эфире хуже всего (зритель видит не то) — первым в статусе. */
const AIR_RANK: Record<OnAirKind, number> = { subs_stuck: 5, partial: 4, subs_only: 3, full: 2, old: 1, none: 0 };

/** Слова отката, вернувшего прежние субтитры (site_server `on_air.why_ru`, `summary_ru`). */
const SUBS_BACK_RU = "откат вернул прежние";

/**
 * У упавшей правки, чьи субтитры выходили, а откат вернул прежнее (`on_air` `old`), — как плеер (строка «В эфире»):
 * «субтитры правки выходили в 09:53, откат вернул прежние». Без `on_air` (полоса старше поля) — «откат вернул прежние»
 * только когда сервер это сказал (`round_revert.subs_restored` или те же слова в отчёте), иначе — «вернул ли их откат,
 * сервер не сказал» (плеер, `failedRoundReport`).
 */
const subsBackLine = (fix: StreamFix, air: FixAir): string => {
    const subs = hhmm(air.subsAt);
    const head = `субтитры правки выходили${subs ? ` в ${subs}` : ""}`;
    const rr = fix.round_revert && typeof fix.round_revert === "object" ? fix.round_revert : null;
    const words = `${rr?.words || ""} ${fix.error || ""} ${fix.summary_ru || ""}`;
    const said = air.fromServer || Boolean(rr?.subs_restored) || words.indexOf(SUBS_BACK_RU) >= 0;
    return said ? `${head}, ${SUBS_BACK_RU}` : `${head} — вернул ли их откат, сервер не сказал`;
};

/**
 * Короткий статус упавшей (или отказанной) правки — по тому, что сейчас в эфире от неё (договор круга AI). «Не вышла в
 * эфир» — только когда от правки в эфир ничего не выходило (`none`); субтитры правки выходили, а откат вернул прежние
 * (`old`), — «не исправлено: субтитры правки выходили в 09:53, откат вернул прежние», как плеер (проверка круга AI,
 * fxf4471865d6b346: статус «не вышла в эфир» спорил с отчётом под ним).
 */
export const failedShort = (fix: StreamFix): string => {
    const air = onAirOf(fix);
    switch (air.kind) {
        case "old":
            return `✕ не исправлено: ${subsBackLine(fix, air)}`;
        case "subs_only":
            return "✕ ошибка: голос не вышел — в эфире только субтитры правки";
        case "subs_stuck":
            return "✕ ошибка отката: прежние субтитры не выложены — зритель может видеть субтитры правки";
        case "partial":
            return "✕ ошибка: в эфире может быть часть правки";
        case "full":
            return "✕ ошибка после выкладки — правка в эфире";
        default:
            return "✕ ошибка: не вышла в эфир";
    }
};

/**
 * Что в эфире от упавшей правки — строкой для подсказки и тоста, когда в эфире что-то от неё есть (у «ничего» и «откат
 * вернул прежнее» — ""): «В эфире: только субтитры правки (вышли в 04:58); голос не вышел».
 */
export const failedAirLine = (fix: StreamFix): string => {
    const air = onAirOf(fix);
    const subs = hhmm(air.subsAt);
    const media = hhmm(air.mediaAt || air.subsAt);
    const why = air.why ? `; ${localTimes(air.why, fixRef(fix))}` : "";
    switch (air.kind) {
        case "subs_only":
            return `В эфире: только субтитры правки${subs ? ` (вышли в ${subs})` : ""} — голос не вышел${why}`;
        case "subs_stuck":
            return (
                `В эфире: субтитры правки${subs ? ` (вышли в ${subs})` : ""} — откат вернул папку, а прежние субтитры ` +
                `не выложены: зритель может видеть субтитры правки${why}`
            );
        case "partial":
            return `В эфире: может быть часть правки${media ? ` (с ${media})` : ""}${why}`;
        case "full":
            return `В эфире: правка${media ? ` с ${media}` : ""}, хотя круг закончился ошибкой${why}`;
        default:
            return "";
    }
};

/**
 * Заголовок тоста упавшей правки — не «не дошла до эфира», когда в эфире что-то от неё есть или её субтитры выходили, а
 * откат вернул прежние (`old`: «Не исправлено (папка 1): субтитры правки выходили в 09:53, откат вернул прежние» — как
 * статус и отчёт в теле тоста).
 */
export const failedToastTitle = (fix: StreamFix, where: string): string => {
    const air = onAirOf(fix);
    switch (air.kind) {
        case "old":
            return `Не исправлено (${where}): ${subsBackLine(fix, air)}`;
        case "subs_only":
            return `Правка вышла не вся (${where}): голос не вышел — в эфире только субтитры правки`;
        case "subs_stuck":
            return `Ошибка отката (${where}): прежние субтитры не выложены — зритель может видеть субтитры правки`;
        case "partial":
            return `Правка вышла не вся (${where}): в эфире может быть часть правки`;
        case "full":
            return `Ошибка после выкладки (${where}): правка в эфире`;
        default:
            return `Правка не дошла до эфира (${where})`;
    }
};

/** Отчёт упавшей правки для тоста и подсказки: строка сервера и, если в эфире что-то от неё есть, — что именно. */
export const failedLine = (fix: StreamFix): string => [fixLine(fix), failedAirLine(fix)].filter(Boolean).join("\n");

export interface FixEvent {
    /**
     * `closed` — вопрос заявки закрыт так, как его считает TAG (`questionState`): ответ кнопкой «оставить как в
     * эфире», новая правка ответа закончилась, сервер закрыл сам. Открытый вопрос (ждёт ответа или ответ принят, а
     * новая правка ещё идёт) — `finished`: его тост с текстом, который не влез, показывается или обновляется.
     */
    kind: "accepted" | "finished" | "closed";
    fix: StreamFix;
}

/**
 * Что помнить о заявке между опросами: состояние, а у вопроса — и его ход (ждёт ответа / ответ принят — и какая новая
 * правка его держит / закрыт): тост вопроса обновляется на каждом шаге хода.
 */
const seenKey = (fix: StreamFix, fixes: StreamFix[]): string => {
    if (fix.state !== ASK_FIX_STATE) return fix.state;
    const q = questionState(fix, fixes);
    return `${fix.state}:${q.phase}${q.phase === "answered" && q.follow ? `:${q.follow.id}` : ""}`;
};

/**
 * Что сказать тостом: новые заявки этого переводчика («принята») и те, что дошли до итога. `seen` — что уже
 * сказано (id → состояние), меняется на месте. Первый проход (`seen` пуст) только запоминает: старые правки
 * при открытии редактора не всплывают — кроме ОТКРЫТОГО вопроса своей правки: после перезагрузки его тост с кнопками
 * и текстом, который не влез, нужен снова (иначе вопрос старше пяти заявок оставался без кнопок, 04.11), — и
 * ЗАКРЫТОГО вопроса, чей текст, который не влез, так и не лёг (`proposedPending`): его тост закрытия висит, пока
 * человек его не закрыл (`dismissed` — какие он закрыл), иначе после перезагрузки текст пропадал с экрана (04.11).
 */
export const fixEvents = (
    fixes: StreamFix[],
    seen: Map<string, string>,
    me: string | null,
    firstPass: boolean,
    dismissed: ReadonlySet<string> = new Set<string>(),
): FixEvent[] => {
    const out: FixEvent[] = [];
    for (const fix of fixes) {
        if (!fix?.id) continue;
        if (me && fix.by && fix.by.toLowerCase() !== me.toLowerCase()) continue;
        const before = seen.get(fix.id);
        const now = seenKey(fix, fixes);
        seen.set(fix.id, now);
        if (before === now) continue;
        const phase = questionState(fix, fixes).phase;
        if (firstPass) {
            if (phase === "asking" || phase === "answered") out.push({ kind: "finished", fix });
            else if (phase === "closed" && !dismissed.has(fix.id) && proposedPending(fix, fixes)) {
                out.push({ kind: "closed", fix });
            } else if (!dismissed.has(fix.id) && isMarkedSameAsking(fix, fixes)) {
                // «Не исправлено — вопрос вам» (P50m20f, E5): после перезагрузки его тост нужен снова, пока человек не
                // вписал свой текст или не закрыл тост сам.
                out.push({ kind: "finished", fix });
            }
            continue;
        }
        if (phase === "closed") {
            out.push({ kind: "closed", fix });
        } else if (before === undefined) {
            out.push({ kind: FINAL_FIX_STATES.includes(fix.state) ? "finished" : "accepted", fix });
        } else if (FINAL_FIX_STATES.includes(fix.state)) {
            out.push({ kind: "finished", fix });
        }
    }
    return out;
};

/** Чьё: заявки этого переводчика (без адреса — все). */
export const isMine = (fix: StreamFix, me: string | null): boolean =>
    !me || !fix.by || fix.by.toLowerCase() === me.toLowerCase();

/** Правки этого переводчика по папке и языку, что ещё идут (их ход видно после «Готово — в эфир»). */
export const openOwnFixIds = (fixes: StreamFix[], me: string | null): string[] =>
    fixes.filter((fix) => fix?.id && isMine(fix, me) && OPEN_FIX_STATES.includes(fix.state)).map((fix) => fix.id);

// ── Одни слова для статуса, тега окна «папки и правки» и заголовка тоста (проверка круга AJ) ──────────────────────
//
// Проверка круга AJ (33 исхода круга правки через редактор): статус рядом с «Готово — в эфир» уже говорил по `on_air`
// («оставлено как есть», «✕ ошибка: голос не вышел — в эфире только субтитры правки»), а тост «оставить как есть»
// (fxe7fcb39a383b4b) — «Правка в эфире (папка 1)», тег окна — машинные fixed / failed / queued / refused / nothing_to_fix.
// Теперь тег и заголовок тоста — те же слова, что статус, по `on_air` и виду правки.

/** «Оставить как есть» (`kind: keep`): текст и голос не менялись, в эфире прежнее. */
export const KEPT_SHORT = "оставлено как есть";
/** Эту часть зрители уже видели — правка в записи эфира (и у зашедших позже). */
export const RECORD_SHORT = "исправлено в записи";
/** Сервер сказал, что в эфире только часть правки (`on_air.kind: partial`). */
export const PART_ON_AIR_SHORT = "в эфире часть правки";

/** Правка «оставить как есть», от которой в эфире ничего не менялось. */
export const isKept = (fix: StreamFix): boolean =>
    fix.state === "fixed" && String(fix.kind || "") === "keep" && onAirOf(fix).kind === "none";

/** Правка в эфире частью — так сказал сервер (`on_air.kind: partial`). */
const isPartOnAir = (fix: StreamFix): boolean => {
    const air = onAirOf(fix);
    return air.fromServer && air.kind === "partial";
};

/** Что откат вернул в эфир у заявки, которую он вернул в очередь, — слова статуса, тега и тоста. */
const ROUND_BACK_OLD = "откат вернул прежний текст";
const ROUND_BACK_STUCK = "прежние субтитры не выложены: зритель может видеть субтитры правки";

/** Откат её круга вернул заявку в очередь, а в эфир — прежнее (`on_air` old / subs_stuck); следующий круг ещё не идёт. */
const roundBackTail = (fix: StreamFix): string => {
    if (FINAL_FIX_STATES.includes(fix.state) || fix.state === "running") return "";
    const kind = onAirOf(fix).kind;
    return kind === "subs_stuck" ? ROUND_BACK_STUCK : kind === "old" ? ROUND_BACK_OLD : "";
};

/** «ждёт следующего круга — откат вернул прежний текст» (статус и тег); откат заявку не возвращал — "". */
export const roundBackShort = (fix: StreamFix): string => {
    const tail = roundBackTail(fix);
    return tail ? `ждёт следующего круга — ${tail}` : "";
};

/** Правка в эфире (`fixed`) — коротко, как статус: «оставлено как есть», «исправлено в записи», «в эфире часть правки». */
export const fixedShort = (fix: StreamFix): string => {
    if (isKept(fix)) return KEPT_SHORT;
    const part = isPartOnAir(fix);
    if (fix.to_record_only) return part ? `${RECORD_SHORT} — часть правки` : RECORD_SHORT;
    return part ? PART_ON_AIR_SHORT : "в эфире";
};

/**
 * Упавшая (отказанная) правка — коротко, теми же словами, что статус (`failedShort`), по тому, что от неё сейчас в
 * эфире: «не вышла в эфир», «голос не вышел — в эфире только субтитры правки», «не исправлено — откат вернул прежние»…
 * Отказ проверки — «отказано — …».
 */
export const failedTagText = (fix: StreamFix): string => {
    const air = onAirOf(fix);
    let words: string;
    switch (air.kind) {
        case "old":
            words =
                subsBackLine(fix, air).indexOf(SUBS_BACK_RU) >= 0
                    ? `не исправлено — ${SUBS_BACK_RU}`
                    : "не исправлено — субтитры правки выходили, вернул ли их откат, сервер не сказал";
            break;
        case "subs_only":
            words = "голос не вышел — в эфире только субтитры правки";
            break;
        case "subs_stuck":
            words = "ошибка отката — зритель может видеть субтитры правки";
            break;
        case "partial":
            words = PART_ON_AIR_RU;
            break;
        case "full":
            words = "ошибка после выкладки — правка в эфире";
            break;
        default:
            words = "не вышла в эфир";
    }
    return fix.state === "refused" ? `отказано — ${words}` : words;
};

/** Слова хода заявки в работе (как плеер, site_eng `FIX_STATE_TITLES`): «принята — в очереди», «идёт круг правки». */
const OPEN_STATE_WORDS: Record<string, string> = {
    queued: "принята — в очереди",
    settling: "ждёт, пока правки папки устоятся",
    running: "идёт круг правки",
};

/**
 * Заявка в работе — коротко: откат вернул её в очередь — как статус («ждёт следующего круга — …»); ждёт прохода папки —
 * «ждёт прохода папки N»; иначе — её ход и что от неё уже в эфире (`on_air`): «идёт круг правки; в эфире пока только
 * субтитры правки».
 */
export const openTagText = (fix: StreamFix): string => {
    const back = roundBackShort(fix);
    if (back) return back;
    const wait = passWaitOf(fix);
    const head = wait ? passWaitTag(wait) : OPEN_STATE_WORDS[fix.state] || "в работе";
    switch (onAirOf(fix).kind) {
        case "subs_only":
            return `${head}; в эфире пока только ${String(fix.kind || "") === "ru" ? "русский" : "субтитры"} правки`;
        case "partial":
            return `${head}; ${PART_ON_AIR_SHORT}`;
        case "full":
            return `${head}; правка уже в эфире`;
        default:
            return head;
    }
};

export interface FixTag {
    /** Цвет тега antd. */
    color: string;
    text: string;
}

/**
 * Тег правки в окне «папки и правки» — словами, теми же, что статус рядом с «Готово — в эфир» и заголовок тоста, по
 * `on_air` (договор круга AI); ни одного машинного fixed / failed / queued / refused / nothing_to_fix. У вопроса — его
 * ход, как его считает TAG (ждёт ответа / ответ принят, а новая правка ещё идёт / закрыт).
 */
export const fixTag = (fx: StreamFix, fixes: StreamFix[] = []): FixTag => {
    const phase = questionState(fx, fixes).phase;
    if (phase === "closed") return { color: "default", text: "вопрос закрыт" };
    if (phase === "answered") return { color: "processing", text: "ответ принят" };
    if (fx.state === ASK_FIX_STATE) return { color: "warning", text: `вопрос вам${askAirTail(fx)}` };
    // Названное переводчиком неверным перевод заново оставил прежним (проверка круга AF, P50m20f E5): не зелёное «в
    // эфире» и не «менять нечего», а вопрос ему — пока он не вписал свой текст этих реплик. Слова — те же, что статус
    // (проверка круга AI, E5): «в эфире с 11:17, но кусок 2 из 3 остался прежним — вопрос вам».
    const same = markedSameState(fx, fixes).phase;
    if (same === "asking") return { color: "warning", text: markedSameShort(fx) || MARKED_SAME_SHORT };
    if (same === "answered") return { color: "processing", text: "не исправлено — идёт ваша правка" };
    if (same === "closed") return { color: "default", text: "вопрос закрыт" };
    if (fx.state === "fixed") {
        if (isKept(fx)) return { color: "default", text: KEPT_SHORT };
        return { color: isPartOnAir(fx) ? "warning" : "green", text: fixedShort(fx) };
    }
    if (OPEN_FIX_STATES.includes(fx.state)) {
        return { color: roundBackTail(fx) === ROUND_BACK_STUCK ? "warning" : "processing", text: openTagText(fx) };
    }
    if (fx.state === "nothing_to_fix") return { color: "default", text: "менять нечего" };
    if (fx.state === "failed" || fx.state === "refused") return { color: "error", text: failedTagText(fx) };
    return { color: "default", text: "ход правки редактору неизвестен — подробности в отчёте" };
};

export type ToastType = "success" | "info" | "warning" | "error";

export interface ToastHead {
    type: ToastType;
    message: string;
}

/**
 * Тост новой заявки: «Правка принята (папка 1)»; откат вернул её в очередь, а в эфир — прежнее, — «Правка ждёт
 * следующего круга (папка 1) — откат вернул прежний текст», как статус.
 */
export const acceptedToastHead = (fix: StreamFix, where: string): ToastHead => {
    const tail = roundBackTail(fix);
    if (tail) {
        return {
            type: tail === ROUND_BACK_STUCK ? "warning" : "info",
            message: `Правка ждёт следующего круга (${where}) — ${tail}`,
        };
    }
    return { type: "info", message: `Правка принята (${where})` };
};

/**
 * Заголовок тоста правки, что дошла до итога «в эфире» (`fixed`), — как статус: «Оставлено как есть (папка 1)» (в эфире
 * ничего не менялось, а не «Правка в эфире»), «Исправлено в записи (папка 1)», «В эфире часть правки (папка 1)».
 */
export const fixedToastHead = (fix: StreamFix, where: string): ToastHead => {
    if (isKept(fix)) return { type: "info", message: `Оставлено как есть (${where})` };
    const short = fixedShort(fix);
    if (short === "в эфире") return { type: "success", message: `Правка в эфире (${where})` };
    return { type: "success", message: `${short.charAt(0).toUpperCase()}${short.slice(1)} (${where})` };
};

/** Заголовок тоста «менять нечего» — как статус и тег. */
export const nothingToastHead = (where: string): ToastHead => ({ type: "info", message: `Менять нечего (${where})` });

/**
 * `unfixed` — названное переводчиком неверным перевод заново оставил прежним (`marked_same`, P50m20f, E5): не исправлено
 * — вопрос ему (вписать свой текст), а не «менять нечего» и не «✓ в эфире». `kept` — «оставить как есть» (`kind: keep`):
 * текст и голос не менялись, в эфире прежнее — «оставлено как есть», а не «✓ в эфире» (проверка круга AI).
 */
export type OwnFixPhase =
    | "none"
    | "accepted"
    | "air"
    | "record"
    | "nothing"
    | "failed"
    | "ask"
    | "answered"
    | "closed"
    | "unfixed"
    | "kept";

export interface OwnFixStatus {
    phase: OwnFixPhase;
    /** Коротко, рядом с кнопкой. */
    text: string;
    /** Подсказка (title). */
    title: string;
}

/**
 * Ход своей правки после «Готово — в эфир»: по заявкам, что ушли этим нажатием (`ids`), и опросу прогресса.
 * «принята» → «в эфире · зрители увидят ЧЧ:ММ»; по-русски, без машинных fixed/refused.
 */
export const ownFixStatus = (fixes: StreamFix[], ids: string[], now: number = Date.now()): OwnFixStatus => {
    if (!ids.length) {
        return { phase: "none", text: "правок нет", title: "Ждущих правок нет — в эфире всё как есть" };
    }
    const byId = new Map(fixes.filter((fix) => fix?.id).map((fix) => [fix.id, fix]));
    const mine = ids.map((id) => byId.get(id));
    if (mine.some((fix) => !fix || !FINAL_FIX_STATES.includes(fix.state))) {
        // Откат круга вернул заявку в очередь, а прежнее — в эфир (`on_air` `old`/`subs_stuck`, проверка круга AI,
        // fxac4834f0590e4e): не «принята», а «ждёт следующего круга» и что в эфире — словами плеера (`fixOnAirShort`).
        // Идёт уже следующий круг (`running`) — его ход, как у любой правки.
        const back = mine
            .filter((fix): fix is StreamFix => Boolean(fix))
            .filter((fix) => !FINAL_FIX_STATES.includes(fix.state) && fix.state !== "running")
            .map((fix) => ({ fix, kind: onAirOf(fix).kind }))
            .filter((one) => one.kind === "old" || one.kind === "subs_stuck")
            .sort((a, b) => AIR_RANK[b.kind] - AIR_RANK[a.kind])[0];
        if (back) return { phase: "accepted", text: roundBackShort(back.fix), title: fixLine(back.fix) };
        return {
            phase: "accepted",
            text: "принята",
            title: "Правка принята: перевод и голос правленых реплик уйдут в эфир сами",
        };
    }
    const done = mine as StreamFix[];
    const phaseOf = (fix: StreamFix) => questionState(fix, fixes).phase;
    const asked = done.find((fix) => phaseOf(fix) === "asking");
    if (asked) return { phase: "ask", text: askShort(asked), title: fixLine(asked) };
    // Ответ принят, а вопрос открыт (новая правка ответа ещё идёт или короткой правки ещё нет) — не серым «закрыт».
    const answered = done.find((fix) => phaseOf(fix) === "answered");
    if (answered) {
        return { phase: "answered", text: answeredShort(answered, fixes), title: answeredLine(answered, fixes) };
    }
    // Упавшая правка — по тому, что от неё сейчас в эфире (`on_air`, договор круга AI; проверка круга AG): голос упал,
    // а субтитры правки вышли, или откат не вернул прежние субтитры, — не «не вышла в эфир» (плеер о той же заявке
    // говорил обратное). Из нескольких упавших — та, от которой в эфире больше всего не того.
    const bad = done
        .filter((fix) => fix.state === "failed" || fix.state === "refused")
        .sort((a, b) => AIR_RANK[onAirOf(b).kind] - AIR_RANK[onAirOf(a).kind])[0];
    if (bad) return { phase: "failed", text: failedShort(bad), title: failedLine(bad) };
    // Проверка круга AF: правка с `marked_same` была «менять нечего» (или «✓ в эфире»), а её отчёт говорит «не
    // исправлено: кусок 2 из 3 … остался прежним» и «вопрос вам». Теперь — так и сказано, тоном вопроса; другие куски
    // фразы в эфире — и это сказано, словами плеера (проверка круга AG).
    const samePhase = (fix: StreamFix) => markedSameState(fix, fixes);
    const unfixed = done.find((fix) => samePhase(fix).phase === "asking");
    if (unfixed) return { phase: "unfixed", text: markedSameShort(unfixed), title: fixLine(unfixed) };
    const sameWait = done.find((fix) => samePhase(fix).phase === "answered");
    if (sameWait) {
        return {
            phase: "answered",
            text: `не исправлено — идёт ваша новая правка${atOf(samePhase(sameWait).follow)}`,
            title: fixLine(sameWait),
        };
    }
    const fixed = done.filter((fix) => fix.state === "fixed");
    const closed = done.find((fix) => phaseOf(fix) === "closed");
    if (!fixed.length && closed) {
        return { phase: "closed", text: closedShort(closedLine(closed, fixes)), title: fixLine(closed) };
    }
    const sameClosed = done.find((fix) => samePhase(fix).phase === "closed");
    if (!fixed.length && sameClosed) {
        return {
            phase: "closed",
            text: `вопрос закрыт: ваша новая правка${atOf(samePhase(sameClosed).follow)}`,
            title: fixLine(sameClosed),
        };
    }
    if (!fixed.length) return { phase: "nothing", text: "менять нечего", title: fixLine(done[0]) };
    // «Оставить как есть» (`kind: keep`, проверка круга AI, fxe7fcb39a383b4b): текст и голос не менялись, в эфире прежнее
    // — «оставлено как есть», как сервер («оставлено как есть: текст и голос не менялись») и плеер, а не «✓ в эфире».
    const changed = fixed.filter((fix) => !isKept(fix));
    if (!changed.length) return { phase: "kept", text: KEPT_SHORT, title: fixed.map(fixLine).join("\n") };
    if (changed.every((fix) => fix.to_record_only)) {
        return { phase: "record", text: `✓ ${RECORD_SHORT}`, title: "Эту часть зрители уже видели — исправлено в записи" };
    }
    const publicAt = changed
        .map((fix) => fix.public_at)
        .filter((at): at is string => Boolean(at) && !Number.isNaN(new Date(at as string).getTime()))
        .sort()
        .pop();
    const viewers = viewersLine(publicAt, now);
    // Сервер сказал, что в эфире только часть правки (`on_air.kind: partial`), — не «✓ в эфире» целиком.
    const part = fixed.some(isPartOnAir);
    const head = part ? `✓ ${PART_ON_AIR_SHORT}` : "✓ в эфире";
    return {
        phase: "air",
        text: viewers ? `${head} · ${viewers}` : head,
        title: fixed.map(fixLine).join("\n"),
    };
};

// ── Вопрос переводчику: кнопки ответа и закрытый вопрос (как в плеере site_eng, `streamFixReport`) ─────────────────

/** Ответ кнопкой (translate-python `POST /stream_editor/v1/fixes/{id}/answer` → site_server `…/fixes/{id}/answer`). */
export type FixAnswer = "shorten_own" | "allow_neighbor" | "keep_on_air";

/** Кнопки, которые знает редактор; чего он не знает — кнопкой не рисуется. */
export const FIX_ANSWER_BUTTONS: FixAnswer[] = ["shorten_own", "allow_neighbor", "keep_on_air"];

/** Ответы, которые закрывают вопрос сразу (TAG `translator_question.CLOSING_ANSWERS`). */
export const CLOSING_ANSWERS = ["keep_on_air", "resolved", "probe_retired"];

/** Предел отхода реплики от слова лектора у правки времени (site_server `edit_timing.MAX_DRIFT_MS`), если вопрос молчит. */
const DRIFT_LIMIT_MS = 2000;

/**
 * Ход вопроса правки — ровно как его считает TAG (`translator_question._status`, его правило держит папку: пока вопрос
 * открыт, склейка не сокращает и не удлиняет текст соседей правки, доска и «Доделать» пишут «у папки открыт вопрос
 * переводчику»):
 *  * `asking` — ответа нет;
 *  * `closed` — «оставить как в эфире», `resolved` (новая правка той же реплики прошла, реплика снова в пределе),
 *    `probe_retired`; «разрешить сократить соседнюю» — когда новая заявка ответа (`answered.fix_id`) закончилась;
 *    «сокращу сам» — когда закончилась новая правка человека (не ИИ) тех же реплик той же папки и языка, поданная не
 *    раньше ответа (допуск 1 с, как в TAG);
 *  * `answered` — ответ принят, а вопрос ещё открыт: новая правка ответа идёт (`follow`) или её ещё нет; ответ, которого
 *    редактор не знает, — тоже открыт (как в TAG).
 * `fixes` — заявки, среди которых искать новую правку (полоса: все папки и языки — сравниваются только той же папки и
 * языка). `follow` — новая правка, которая держит или закрыла вопрос.
 */
export type QuestionPhase = "none" | "asking" | "answered" | "closed";

export interface QuestionState {
    phase: QuestionPhase;
    follow?: StreamFix | null;
}

const LANG_ALIAS: Record<string, string> = { ch: "zh" };
/** Один язык (китайский редактора «ch» — это «zh» эфира). */
export const sameLang = (a?: string, b?: string): boolean => {
    const x = String(a || "").trim().toLowerCase();
    const y = String(b || "").trim().toLowerCase();
    return (LANG_ALIAS[x] || x) === (LANG_ALIAS[y] || y);
};

const sameFolderLang = (a: StreamFix, b: StreamFix): boolean =>
    Number(a.folder || 0) === Number(b.folder || 0) && sameLang(a.lang, b.lang);

/** Тот же человек (кого нет — не спорим: считаем тем же). */
const sameBy = (a?: string | null, b?: string | null): boolean => {
    const x = String(a || "").trim().toLowerCase();
    const y = String(b || "").trim().toLowerCase();
    return !x || !y || x === y;
};

/** ISO → секунды (как TAG `_iso`): микросекунды отрезаются до мс, нет времени — 0. */
const isoSeconds = (value?: string | null): number => {
    const raw = String(value || "").trim();
    if (!raw) return 0;
    const ms = Date.parse(raw.replace(/(\.\d{3})\d+/, "$1"));
    return Number.isFinite(ms) ? ms / 1000 : 0;
};

/** Реплики правки — какими они были и какими стали (TAG `translator_question.edit_cues`), и реплики её вопроса. */
export const editCues = (fix: StreamFix): Set<string> => {
    const out = new Set<string>();
    const add = (value: unknown) => {
        if (value !== null && value !== undefined && String(value)) out.add(String(value));
    };
    (fix.cue_ids || []).forEach(add);
    Object.entries(fix.reopened || {}).forEach(([cue, parent]) => {
        add(cue);
        add(parent);
    });
    Object.entries(fix.relaid || {}).forEach(([parent, pieces]) => {
        add(parent);
        (pieces || []).forEach(add);
    });
    (fix.revoiced || []).forEach(add);
    (fix.text_changed || []).forEach(add);
    Object.keys(fix.question?.proposed || {}).forEach(add);
    (fix.question?.own || []).forEach((item) => add(item?.id));
    return out;
};

export const questionState = (fix: StreamFix, fixes: StreamFix[] = []): QuestionState => {
    if (fix.state !== ASK_FIX_STATE) return { phase: "none" };
    const answered = fix.answered;
    if (!answered) return { phase: "asking" };
    const answer = String(answered.answer || "");
    if (CLOSING_ANSWERS.includes(answer)) {
        const id = String(answered.fix_id || "");
        return { phase: "closed", follow: (id && fixes.find((one) => one.id === id)) || null };
    }
    if (answer === "allow_neighbor") {
        const id = String(answered.fix_id || "");
        const follow = id ? fixes.find((one) => one.id === id) : undefined;
        if (follow && !OPEN_FIX_STATES.includes(follow.state)) return { phase: "closed", follow };
        return { phase: "answered", follow: follow || null };
    }
    if (answer === "shorten_own") {
        const at = isoSeconds(answered.at);
        const cues = editCues(fix);
        let running: StreamFix | null = null;
        for (const other of fixes) {
            if (!other?.id || other.id === fix.id || String(other.source || "") === "ai" || !sameFolderLang(other, fix)) {
                continue;
            }
            if (isoSeconds(other.requested_at) + 1 < at) continue;
            if (!Array.from(editCues(other)).some((cue) => cues.has(cue))) continue;
            if (OPEN_FIX_STATES.includes(other.state)) {
                if (!running) running = other;
                continue;
            }
            return { phase: "closed", follow: other };
        }
        return { phase: "answered", follow: running };
    }
    return { phase: "answered", follow: null };
};

/** Вопрос ждёт ответа. Полоса старше поля `question` — по состоянию заявки. */
export const isAsking = (fix: StreamFix): boolean => fix.state === ASK_FIX_STATE && !fix.answered;

/** Вопрос открыт по правилу TAG: ждёт ответа или ответ принят, а новая правка ещё не закончилась. */
export const isQuestionOpen = (fix: StreamFix, fixes: StreamFix[] = []): boolean => {
    const phase = questionState(fix, fixes).phase;
    return phase === "asking" || phase === "answered";
};

/** Вопрос закрыт по правилу TAG (см. `questionState`). */
export const isQuestionClosed = (fix: StreamFix, fixes: StreamFix[] = []): boolean =>
    questionState(fix, fixes).phase === "closed";

/** Вопрос правки времени: реплика встала бы дальше предела от слова лектора. */
export const isDriftQuestion = (fix: StreamFix): boolean => fix.question?.cause?.kind === "edit_drift";

const isRepeatQuestion = (fix: StreamFix): boolean => fix.question?.cause?.kind === "repeat_neighbor";

/** Кнопки ответа на открытый вопрос — ровно `question.answers` сервера, в его порядке; закрыт или вопроса нет — пусто. */
export const questionAnswers = (fix: StreamFix): FixAnswer[] => {
    if (!isAsking(fix)) return [];
    const raw = fix.question?.answers;
    if (!Array.isArray(raw)) return [];
    const out: FixAnswer[] = [];
    for (const one of raw) {
        const answer = String(one) as FixAnswer;
        if (FIX_ANSWER_BUTTONS.includes(answer) && !out.includes(answer)) out.push(answer);
    }
    return out;
};

/** Секунды с сотыми: 2000 мс → «2,00». */
const seconds2 = (ms: number): string => {
    const cs = Math.floor((Math.round(ms) + 5) / 10);
    return `${Math.floor(cs / 100)},${String(cs % 100).padStart(2, "0")}`;
};

const driftLimitRu = (fix: StreamFix): string => {
    const ms = fix.question?.cause?.limit_ms;
    return `${seconds2(typeof ms === "number" && ms > 0 ? ms : DRIFT_LIMIT_MS)} с`;
};

/** Надпись кнопки ответа. */
export const answerTitle = (answer: FixAnswer, fix: StreamFix): string => {
    const many = (fix.question?.neighbors || []).length > 1;
    const repeat = isRepeatQuestion(fix);
    if (answer === "keep_on_air") return "Оставить как в эфире";
    if (answer === "shorten_own") return repeat ? "Исправить свой текст" : "Сократить свой текст";
    if (answer === "allow_neighbor") return `Разрешить ${repeat ? "снять" : "сократить"} ${many ? "соседние" : "соседнюю"}`;
    return String(answer);
};

/** Под кнопками открытого вопроса: что сейчас в эфире и как ещё на него ответить. */
export const questionNote = (fix: StreamFix): string => {
    if (isDriftQuestion(fix)) {
        const keep = questionAnswers(fix).includes("keep_on_air");
        return (
            "Ваше время не выложено, эфир идёт. Поставьте реплику ближе к слову лектора здесь, на ленте, — новая " +
            "правка уйдёт сама и закроет вопрос" +
            (keep
                ? "; или оставьте как в эфире — ничего не изменится."
                : ". Кнопки «Оставить как в эфире» нет: папку не удалось вернуть к тому, что в эфире.")
        );
    }
    const verb = isRepeatQuestion(fix) ? "не снимаются" : "не сокращаются";
    return `Соседние реплики без вашего согласия ${verb}. Пока нет ответа, в эфире — прежняя версия папки.`;
};

/** Текст правки, который не влез (папка откатана — на ленте его уже нет): показать, чтобы он не пропал молча. */
export const proposedTexts = (fix: StreamFix): string[] =>
    Object.values(fix.question?.proposed || {})
        .map((text) => String(text || "").trim())
        .filter(Boolean);

/** ЧЧ:ММ подачи новой правки, если она известна: « (21:12)». */
const atOf = (follow?: StreamFix | null): string => {
    const at = hhmm(follow?.requested_at);
    return at ? ` (${at})` : "";
};

/**
 * Новая правка «сокращу сам» — чья: своя — «ваша новая правка (21:12)»; второго переводчика того же круга (реплика из
 * `question.own`, TAG закрывает и ею) — «новая правка другого переводчика (адрес, 21:12)», а не «ваша».
 */
const ownFollowWord = (fix: StreamFix, follow?: StreamFix | null): string => {
    if (follow && !sameBy(follow.by, fix.by)) {
        const at = hhmm(follow.requested_at);
        return `новая правка другого переводчика (${follow.by}${at ? `, ${at}` : ""})`;
    }
    return `ваша новая правка${atOf(follow)}`;
};

/** Чем закончилась новая правка, закрывшая вопрос: «в эфире 21:14», «менять нечего», «не вышла в эфир: …». */
const followOutcome = (follow?: StreamFix | null): string => {
    if (!follow) return "";
    switch (follow.state) {
        case "fixed": {
            const at = hhmm(follow.published_at || follow.subs_published_at);
            return follow.to_record_only ? "исправлено в записи" : `в эфире${at ? ` ${at}` : ""}`;
        }
        case "nothing_to_fix":
            return "менять нечего";
        case ASK_FIX_STATE:
            return "не влезла и она — у неё свой вопрос вам";
        case "failed":
        case "refused":
            return `не вышла в эфир${follow.error ? `: ${follow.error}` : ""}`;
        default:
            return follow.state;
    }
};

const neighbourWord = (fix: StreamFix): string =>
    `${isRepeatQuestion(fix) ? "снять" : "сократить"} ${(fix.question?.neighbors || []).length > 1 ? "соседние" : "соседнюю"}`;

/**
 * Ответ принят, а вопрос открыт (TAG держит его, пока новая правка не закончилась): что сейчас. Вопрос ждёт ответа
 * или закрыт — "".
 */
export const answeredLine = (fix: StreamFix, fixes: StreamFix[] = []): string => {
    const state = questionState(fix, fixes);
    if (state.phase !== "answered") return "";
    const answer = String(fix.answered?.answer || "");
    if (answer === "allow_neighbor") {
        return state.follow || fix.answered?.fix_id
            ? `Ответ принят: вы разрешили ${neighbourWord(fix)} — идёт новая правка${atOf(state.follow)}; вопрос открыт, ` +
                  "пока она не закончится"
            : `Ответ принят: вы разрешили ${neighbourWord(fix)} — новой правки сервер не поставил; вопрос открыт`;
    }
    if (answer === "shorten_own") {
        return state.follow
            ? `Ответ принят: вы сокращаете свой текст — идёт ${ownFollowWord(fix, state.follow)}` +
                  `${sameBy(state.follow.by, fix.by) ? "" : " тех же реплик"}; вопрос открыт, пока она не закончится`
            : "Ждём вашу короткую правку: сократите текст реплики здесь, на ленте, и сохраните — новая правка уйдёт в " +
                  "эфир сама; вопрос открыт, пока она не закончится";
    }
    return `Ответ «${answer || "?"}» принят — какой, редактор не знает; вопрос считается открытым`;
};

/** Коротко (рядом с «Готово — в эфир»): «ответ принят: идёт новая правка», «ждём вашу короткую правку». */
export const answeredShort = (fix: StreamFix, fixes: StreamFix[] = []): string => {
    const state = questionState(fix, fixes);
    if (state.phase !== "answered") return "";
    if (String(fix.answered?.answer || "") === "shorten_own" && !state.follow) return "ждём вашу короткую правку";
    return `ответ принят: идёт новая правка${atOf(state.follow)}`;
};

/**
 * Закрытый вопрос словами: «Вопрос закрыт: …». Новая правка, закрывшая вопрос, — из `fixes` (её время и итог).
 * Вопрос открыт — "".
 */
export const closedLine = (fix: StreamFix, fixes: StreamFix[] = []): string => {
    const state = questionState(fix, fixes);
    if (state.phase !== "closed") return "";
    const answered: FixAnswered = fix.answered || {};
    const answer = String(answered.answer || "");
    const newFix = `новая правка${atOf(state.follow)}`;
    const outcome = followOutcome(state.follow);
    switch (answer) {
        case "keep_on_air":
            return "Вопрос закрыт: оставлено как в эфире — место реплики прежнее, ничего не менялось";
        case "probe_retired":
            return "Вопрос закрыт: проба снята — круг правки не запускался, в эфир ничего не ушло";
        case "allow_neighbor":
            return `Вопрос закрыт: ${newFix} с разрешением ${neighbourWord(fix)} закончилась${outcome ? ` — ${outcome}` : ""}`;
        case "shorten_own":
            return `Вопрос закрыт: ${ownFollowWord(fix, state.follow)} тех же реплик закончилась${outcome ? ` — ${outcome}` : ""}`;
        case "resolved": {
            const how = String(answered.how || "");
            if (how === "time") {
                return `Вопрос закрыт: реплику поставила ${newFix} — её место выложено и сверено с выравниванием`;
            }
            if (how === "keep") return `Вопрос закрыт: ${newFix} оставила реплику как есть`;
            if (how === "in_limit") return `Вопрос закрыт: реплика снова у слова лектора (в пределе ${driftLimitRu(fix)})`;
            return `Вопрос закрыт: реплику выложила ${newFix}`;
        }
        default:
            return `Вопрос закрыт (ответ «${answer || "?"}»)`;
    }
};

/** Коротко (рядом с «Готово — в эфир»): «вопрос закрыт: оставлено как в эфире». */
export const closedShort = (line: string): string => line.replace(/^Вопрос закрыт/, "вопрос закрыт").split(" — ")[0];

/**
 * Закрытый вопрос с текстом, который не влез (и после «оставить как в эфире», и после новой правки): этот текст
 * переводчика в папке так и не лёг — он виден, с подсказкой, что его можно вписать снова (п.50г: текст переводчика не
 * пропадает молча, и с экрана).
 */
export const PROPOSED_AGAIN_HINT = "его можно вписать снова: исправьте реплику здесь, на ленте, и сохраните — новая правка уйдёт в эфир сама";

/**
 * Что в эфире от правки с вопросом, где это меняет слова, — по `on_air` сервера (без поля — молчим: у вопроса метка
 * субтитров остаётся и после отката, вернувшего прежние): откат не вернул прежние субтитры — «; зритель может видеть
 * субтитры правки» (как плеер), папку не откатить — «; в эфире только субтитры правки» (проверка круга AI,
 * fxc35102a0e6054c, fx57b12721b42643).
 */
const askAirTail = (fix: StreamFix): string => {
    const air = onAirOf(fix);
    if (!air.fromServer) return "";
    if (air.kind === "subs_stuck") return "; зритель может видеть субтитры правки";
    if (air.kind === "subs_only") return "; в эфире только субтитры правки";
    return "";
};

/** Коротко об открытом вопросе (рядом с «Готово — в эфир»): что ответить — по кнопкам вопроса; и что в эфире. */
export const askShort = (fix: StreamFix): string => {
    const tail = askAirTail(fix);
    if (!fix.question) {
        return `⚠ не влезает — сократите текст или разрешите сократить соседнюю (плеер, «Мои правки»)${tail}`;
    }
    const titles = questionAnswers(fix).map((answer) => `«${answerTitle(answer, fix)}»`);
    if (isDriftQuestion(fix)) {
        return `⚠ время не выложено — поставьте реплику ближе к слову лектора${titles.length ? ` или ${titles.join(" или ")}` : ""}${tail}`;
    }
    return `⚠ не влезает — ${titles.length ? titles.join(" или ") : "сократите свой текст"}${tail}`;
};

/** Ответ кнопкой принят, а опрос ещё не принёс его: что теперь (вопрос открыт, пока новая правка не закончится). */
export const answeredHere = (answer: FixAnswer, fix: StreamFix): string => {
    if (answer === "keep_on_air") {
        return "Вопрос закрыт: оставлено как в эфире — место реплики прежнее, ничего не менялось";
    }
    if (answer === "allow_neighbor") {
        return (
            `Ответ принят: новая правка того же текста ${isRepeatQuestion(fix) ? "снимет повтор у соседней" : "сократит соседнюю"} ` +
            "— её ход в «Эфир: папки и правки»; вопрос открыт, пока она не закончится"
        );
    }
    return (
        "Ответ принят — ждём вашу короткую правку: сократите текст реплики здесь, на ленте, и сохраните — новая правка " +
        "уйдёт в эфир сама; вопрос открыт, пока она не закончится"
    );
};

/** Правки, которые текст реплики не пишут: время, ♪, снятие с озвучки; вид «голос» и «оставить как есть». */
const NOT_TEXT_EDITS = ["time", "revoice", "skip", "unskip", "delete_audio"];
const writesText = (fix: StreamFix): boolean => {
    const kind = String(fix.kind || "");
    return !NOT_TEXT_EDITS.includes(String(fix.edit || "")) && !kind.startsWith("vo.") && kind !== "keep";
};

/**
 * Текст, который не влез, ушёл в новую правку: «разрешить сократить соседнюю» — тот же текст ушёл новой заявкой ответа,
 * и она закончилась; иначе — тот же переводчик потом переписал текст этих реплик (новая правка текста той же папки и
 * языка, не ИИ, поданная позже вопроса и не раньше ответа, закончилась; не влезла и она — её текст на экране её
 * вопросом). После «оставить как в эфире» без новой правки — не ушёл.
 */
export const proposedCarried = (fix: StreamFix, fixes: StreamFix[] = []): boolean => {
    if (!proposedTexts(fix).length) return true;
    const state = questionState(fix, fixes);
    if (String(fix.answered?.answer || "") === "allow_neighbor" && state.phase === "closed" && state.follow) return true;
    const keys = Object.keys(fix.question?.proposed || {});
    const since = isoSeconds(fix.answered?.at || fix.finished_at || fix.requested_at);
    return fixes.some(
        (other) =>
            Boolean(other?.id) &&
            other.id !== fix.id &&
            String(other.source || "") !== "ai" &&
            sameFolderLang(other, fix) &&
            sameBy(other.by, fix.by) &&
            !OPEN_FIX_STATES.includes(other.state) &&
            isoSeconds(other.requested_at) > isoSeconds(fix.requested_at) &&
            isoSeconds(other.requested_at) + 1 >= since &&
            writesText(other) &&
            Array.from(editCues(other)).some((cue) => keys.includes(cue)),
    );
};

/**
 * Вопрос закрыт, а текст переводчика, который не влез, так и не лёг и в новую правку не ушёл (п.50г: текст не
 * пропадает молча, и с экрана): его держат видимым — тостом и в окне «папки и правки», — пока человек не закрыл тост.
 */
export const proposedPending = (fix: StreamFix, fixes: StreamFix[] = []): boolean =>
    questionState(fix, fixes).phase === "closed" && proposedTexts(fix).length > 0 && !proposedCarried(fix, fixes);

// ── Названное переводчиком неверным перевод заново оставил прежним (`marked_same`, проба P50m20f, E5) ──────────────
//
// Проверка круга AF: у такой правки редактор писал «менять нечего» (или «✓ в эфире» — у фразы сменились другие куски),
// а отчёт той же правки — «не исправлено: кусок 2 из 3, на который вы показали, остался прежним … вопрос вам: впишите
// свой». Теперь статус и тост — «не исправлено — вопрос вам», текст вопроса виден, а «К реплике» выделяет этот кусок на
// ленте и открывает правку его текста.

/** Коротко (статус рядом с «Готово — в эфир», тег окна «папки и правки», заголовок тоста). */
export const MARKED_SAME_SHORT = "не исправлено — вопрос вам";

/** «1 и 3», «1, 2 и 4». */
const andRu = (items: number[]): string =>
    items.length > 1 ? `${items.slice(0, -1).join(", ")} и ${items[items.length - 1]}` : items.map(String).join("");

/**
 * Что осталось прежним — коротко, словами плеера (site_eng `markedSameWhatRu`): «кусок 2 из 3 остался прежним»,
 * «куски 1 и 3 из 3 остались прежними», «реплика, на которую вы показали, осталась прежней».
 */
export const markedSameWhatShort = (items: MarkedSame[]): string => {
    const pieces = items.filter((item) => item.index && item.of);
    const plain = items.length - pieces.length;
    const parts: string[] = [];
    if (pieces.length === 1) {
        parts.push(`кусок ${pieces[0].index} из ${pieces[0].of} остался прежним`);
    } else if (pieces.length > 1 && new Set(pieces.map((item) => Number(item.of))).size === 1) {
        const idx = pieces.map((item) => Number(item.index)).sort((a, b) => a - b);
        parts.push(`куски ${andRu(idx)} из ${pieces[0].of} остались прежними`);
    } else if (pieces.length > 1) {
        const k = pieces.length;
        parts.push(
            `${k} ${pluralRu(k, "кусок", "куска", "кусков")} ${pluralRu(k, "остался прежним", "остались прежними", "остались прежними")}`,
        );
    }
    if (plain === 1) {
        parts.push("реплика, на которую вы показали, осталась прежней");
    } else if (plain > 1) {
        parts.push(
            `${cuesRu(plain)}, ${pluralRu(plain, "на которую", "на которые", "на которые")} вы показали, ` +
                pluralRu(plain, "осталась прежней", "остались прежними", "остались прежними"),
        );
    }
    return parts.join(", ");
};

/**
 * Коротко о правке, чей названный неверным кусок остался прежним (статус рядом с «Готово — в эфир», заголовок тоста) —
 * словами плеера (site_eng `markedSameShort`, проверка круга AG): другие куски фразы вышли в эфир — «в эфире с 06:33, но
 * кусок 2 из 3 остался прежним — вопрос вам»; ничего не вышло — «не исправлено: кусок 2 из 3 остался прежним — вопрос
 * вам» (у реплики — «не исправлено — вопрос вам»). Что в эфире — из `on_air` сервера, без него — по итогу круга.
 */
export const markedSameShort = (fix: StreamFix): string => {
    const items = markedSameOf(fix);
    if (!items.length) return "";
    const what = markedSameWhatShort(items);
    const air = onAirOf(fix);
    if (fix.state === "fixed" && (air.kind === "partial" || air.kind === "full")) {
        const at = hhmm(air.mediaAt || air.subsAt || fix.published_at || fix.subs_published_at);
        return `в эфире${at ? ` с ${at}` : ""}, но ${what} — вопрос вам`;
    }
    return items.some((item) => item.index && item.of) ? `не исправлено: ${what} — вопрос вам` : MARKED_SAME_SHORT;
};

/**
 * «Исправлено» правки, чей кусок остался прежним, когда остальное в эфире (тост вопроса): «В эфире с 06:33: фраза из 3
 * кусков переведена заново целиком …» — часть «исправлено: …» отчёта сервера. Ничего не вышло — "".
 */
export const markedSameAirLine = (fix: StreamFix): string => {
    if (!markedSameOf(fix).length || fix.state !== "fixed") return "";
    const air = onAirOf(fix);
    if (air.kind !== "partial" && air.kind !== "full") return "";
    const at = hhmm(air.mediaAt || air.subsAt || fix.published_at || fix.subs_published_at);
    const part = summaryFixedPart(fix.summary_ru);
    const what = part && part !== "нет" ? part : air.why ? localTimes(air.why, fixRef(fix)) : "остальное из правки";
    return `В эфире${at ? ` с ${at}` : ""}: ${fixCueCounts(what)}`;
};

/** Правка, у которой бывает `marked_same` (site_server считает его у `fixed` и `nothing_to_fix`). */
const MARKED_SAME_STATES = ["fixed", "nothing_to_fix"];

/** Часть отчёта сервера (`_marked_same_ru`): «не исправлено: кусок 2 из 3, на который вы показали, остался прежним: «…»; почему: …». */
const SAME_SUMMARY_RE =
    /не исправлено: (?:кусок (\d+) из (\d+), на который вы показали,|перевод реплики, на которую вы показали,) остался прежним: «([^»]*)»(?:; почему: ((?:(?!; не исправлено: |; вопрос вам: ).)*))?/g;

/**
 * Что из названного переводчиком осталось прежним. Поле `marked_same` сервера; нет поля (полоса translate-python его не
 * несёт) — из отчёта той же правки (`summary_ru`), а кусок на его месте сейчас — по раскладке заявки: `reopened` (кусок →
 * его фраза) и `relaid` (фраза → её куски сейчас; на месте `index` — кусок `now`).
 */
export const markedSameOf = (fix: StreamFix): MarkedSame[] => {
    if (!fix || !MARKED_SAME_STATES.includes(fix.state)) return [];
    if (Array.isArray(fix.marked_same)) {
        return fix.marked_same.filter((item) => Boolean(item && String(item.text || "").trim()));
    }
    const summary = String(fix.summary_ru || "");
    if (summary.indexOf("не исправлено: ") < 0) return [];
    const marked = (fix.cue_ids || []).map(String);
    const relaid = fix.relaid || {};
    const pieceNow = (index: number, of: number): string | null => {
        const parent = marked.length === 1 ? (fix.reopened || {})[marked[0]] : undefined;
        const own = parent ? relaid[String(parent)] : undefined;
        const lists = own ? [own] : Object.values(relaid).filter((list) => Array.isArray(list) && list.length === of);
        const list = lists.length === 1 ? lists[0] : undefined;
        return list && list.length === of && list[index - 1] ? String(list[index - 1]) : null;
    };
    const out: MarkedSame[] = [];
    const re = new RegExp(SAME_SUMMARY_RE.source, "g");
    let m: RegExpExecArray | null = re.exec(summary);
    while (m) {
        const index = m[1] ? Number(m[1]) : null;
        const of = m[2] ? Number(m[2]) : null;
        out.push({
            cue: marked.length === 1 ? marked[0] : null,
            index,
            of,
            text: m[3],
            now: index && of ? pieceNow(index, of) : null,
            why_ru: m[4] ? m[4].trim() : null,
        });
        m = re.exec(summary);
    }
    return out;
};

/** «кусок 2 из 3, на который вы показали, остался прежним: «…»; почему: …» (как `_marked_same_ru` сервера). */
export const markedSameWhat = (item: MarkedSame): string => {
    const what =
        item.index && item.of
            ? `кусок ${item.index} из ${item.of}, на который вы показали,`
            : "перевод реплики, на которую вы показали,";
    const why = String(item.why_ru || "").trim();
    return `${what} остался прежним: «${String(item.text || "").trim()}»${why ? `; почему: ${why}` : ""}`;
};

/** Вопрос переводчику — в редакторе: вписать свой текст здесь, на ленте («К реплике»). */
export const markedSameQuestion = (items: MarkedSame[]): string => {
    const pieces = items.some((item) => Boolean(item.index));
    const where =
        items.length > 1
            ? `«К реплике» у каждого ${pieces ? "куска" : "из них"} выделит его на ленте и откроет правку его текста`
            : pieces
              ? "«К реплике» выделит этот кусок на ленте и откроет правку его текста"
              : "«К реплике» выделит эту реплику на ленте и откроет правку её текста";
    return `Вопрос вам: если этот перевод неверный, впишите свой — ${where}; сохраните, и новая правка уйдёт в эфир сама.`;
};

/** Имена реплики на ленте, по которым её искать: кусок на его месте сейчас, потом тот, на который показали. */
export const markedSameCues = (item: MarkedSame): string[] => {
    const out: string[] = [];
    [item.now, item.cue].forEach((one) => {
        const name = String(one || "").trim();
        if (name && out.indexOf(name) < 0) out.push(name);
    });
    return out;
};

export type MarkedSamePhase = "none" | "asking" | "answered" | "closed";

/**
 * Ход вопроса «впишите свой»: `asking` — нового текста этих реплик ещё нет; `answered` — новая правка текста тех же
 * реплик (человека, не ИИ, той же папки и языка, поданная позже) идёт; `closed` — она закончилась. `follow` — она.
 */
export const markedSameState = (
    fix: StreamFix,
    fixes: StreamFix[] = [],
): { phase: MarkedSamePhase; follow?: StreamFix | null } => {
    const items = markedSameOf(fix);
    if (!items.length) return { phase: "none" };
    const cues = new Set<string>();
    items.forEach((item) => markedSameCues(item).forEach((cue) => cues.add(cue)));
    let running: StreamFix | null = null;
    for (const other of fixes) {
        if (!other?.id || other.id === fix.id || String(other.source || "") === "ai" || !sameFolderLang(other, fix)) {
            continue;
        }
        if (!sameBy(other.by, fix.by) || !writesText(other)) continue;
        if (isoSeconds(other.requested_at) <= isoSeconds(fix.requested_at)) continue;
        if (!Array.from(editCues(other)).some((cue) => cues.has(cue))) continue;
        if (OPEN_FIX_STATES.includes(other.state)) {
            if (!running) running = other;
            continue;
        }
        return { phase: "closed", follow: other };
    }
    return running ? { phase: "answered", follow: running } : { phase: "asking" };
};

/** «Не исправлено» правки ждёт ответа переводчика. */
export const isMarkedSameAsking = (fix: StreamFix, fixes: StreamFix[] = []): boolean =>
    markedSameState(fix, fixes).phase === "asking";

/** Слова для «ответ/закрыт» вопроса «впишите свой»: «Ваша новая правка (21:12) этого куска идёт», «… — в эфире 21:14». */
export const markedSameFollowLine = (fix: StreamFix, fixes: StreamFix[] = []): string => {
    const state = markedSameState(fix, fixes);
    if (state.phase === "answered") {
        return `Не исправлено — ${ownFollowWord(fix, state.follow)} тех же реплик идёт; вопрос открыт, пока она не закончится`;
    }
    if (state.phase === "closed") {
        const outcome = followOutcome(state.follow);
        return `Вопрос закрыт: ${ownFollowWord(fix, state.follow)} тех же реплик закончилась${outcome ? ` — ${outcome}` : ""}`;
    }
    return "";
};

/**
 * Строка реплики на ленте, которую назвал отчёт: по имени (кусок на его месте сейчас, потом тот, на который показали),
 * иначе — по тексту перевода (те же слова, что «остался прежним»). Нет — null.
 */
export const findMarkedRow = <T extends { id?: string; original_id?: string; translation?: string | null }>(
    rows: T[] | undefined,
    item: MarkedSame,
): T | null => {
    const list = rows || [];
    for (const name of markedSameCues(item)) {
        const hit = list.find((row) => row && (row.original_id === name || row.id === name));
        if (hit) return hit;
    }
    const words = textWords(item.text);
    if (!words) return null;
    const same = list.filter((row) => row && textWords(row.translation) === words);
    return same.length === 1 ? same[0] : null;
};

/** Слова текста для сверки «те же ли слова»: строчные, без знаков препинания, апостроф один. */
const textWords = (text?: string | null): string =>
    String(text || "")
        .toLowerCase()
        .replace(/[’‘]/g, "'")
        .replace(/[\s.,!?;:"«»“”„()[\]{}…—–-]+/g, " ")
        .trim();

/** Сколько последних правок папки видно в окне «папки и правки»; открытые вопросы видны всегда, сверх этого. */
export const SHOWN_FIXES = 5;

/**
 * Правки папки в окне «папки и правки»: открытые вопросы (по правилу TAG) — всегда и первыми, хоть они старше пяти
 * последних заявок («текст + время» дают по две заявки — вопрос уезжал за срез и оставался без кнопок, 04.11); так же —
 * закрытый вопрос, чей текст, который не влез, так и не лёг (`proposedPending`), пока человек не закрыл его тост
 * (`dismissed`); дальше — последние `SHOWN_FIXES`.
 */
export const visibleFixes = (fixes: StreamFix[], dismissed: ReadonlySet<string> = new Set<string>()): StreamFix[] => {
    const recent = fixes.slice(0, SHOWN_FIXES);
    const older = fixes
        .slice(SHOWN_FIXES)
        .filter(
            (fx) =>
                (Boolean(fx.question) &&
                    (isQuestionOpen(fx, fixes) || (!dismissed.has(fx.id) && proposedPending(fx, fixes)))) ||
                // «Не исправлено — вопрос вам» без ответа — тоже открытый вопрос (P50m20f, E5).
                (!dismissed.has(fx.id) && isMarkedSameAsking(fx, fixes)),
        );
    return [...older, ...recent];
};
